import { batchMarketPrice } from '@/api/trade';
import { getFeedCardPrices } from '@/api/market';
import {
  collectFeedCardPriceRequests,
  applyFeedCardPrices,
  FEED_CARD_PRICE_MAX_COLLECTIONS,
} from '@/domain/market/feed-card-prices.js';
import {
  applyBatchPriceMapToFeedRows,
  chunkFeedBatchPriceItems,
  collectFeedBatchPriceItems,
  FEED_BATCH_PRICE_CHUNK_SIZE,
  FEED_BATCH_PRICE_MAX_ITEMS,
} from '@/domain/market/feed-batch-price.js';

/** store → 是否有后台 FairOdds 刷新进行中（不阻塞列表） */
const inflightByStore = new WeakMap();
const pendingByStore = new WeakMap();

function deferIdle(run) {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => run(), { timeout: 2000 });
    return;
  }
  setTimeout(run, 0);
}

/**
 * 后台跑一轮 batchPrice 写回 rawRows；失败静默。
 * 不设 listLoading / loadingMore，不 await 于调用方关键路径。
 */
function isFeedBusy(store) {
  return Boolean(store?.listLoading) || Boolean(store?.loadingMore);
}

async function runFeedFairOddsRefresh(store, options = {}) {
  if (!store) return;
  const active = inflightByStore.get(store);
  if (active) {
    // An append/filter change during a request must get its own refresh pass.
    if (store.rawRows !== active.rows || (Number(store.loadGeneration) || 0) !== active.gen) {
      pendingByStore.set(store, options);
    }
    return;
  }
  if (!store.initialized) return;
  if (isFeedBusy(store)) return;

  const rows = store.rawRows;
  if (!Array.isArray(rows) || !rows.length) return;

  const items = collectFeedBatchPriceItems(rows, {
    maxItems: options.maxItems ?? FEED_BATCH_PRICE_MAX_ITEMS,
  });
  const previewRequests = collectFeedCardPriceRequests(rows);
  if (!items.length && !previewRequests.length) return;

  const gen = Number(store.loadGeneration) || 0;
  const activeState = { rows, gen };
  inflightByStore.set(store, activeState);
  const isCurrent = () =>
    (Number(store.loadGeneration) || 0) === gen &&
    !isFeedBusy(store) &&
    store.rawRows === activeState.rows;

  try {
    for (
      let offset = 0;
      offset < previewRequests.length;
      offset += FEED_CARD_PRICE_MAX_COLLECTIONS
    ) {
      if (!isCurrent()) return;
      const chunk = previewRequests.slice(offset, offset + FEED_CARD_PRICE_MAX_COLLECTIONS);
      try {
        const result = await getFeedCardPrices(chunk);
        if (!isCurrent()) return;
        if (applyFeedCardPrices(store.rawRows, result, chunk)) {
          store.rawRows = store.rawRows.slice();
          activeState.rows = store.rawRows;
        }
      } catch (err) {
        console.warn('[feed:cardPrices] refresh failed', err);
      }
    }
    if (!isCurrent()) return;
    const chunks = chunkFeedBatchPriceItems(
      items,
      options.chunkSize ?? FEED_BATCH_PRICE_CHUNK_SIZE
    );

    for (const chunk of chunks) {
      if ((Number(store.loadGeneration) || 0) !== gen) return;
      if (isFeedBusy(store)) return;

      let batchMap;
      try {
        batchMap = await batchMarketPrice(chunk);
      } catch (err) {
        console.warn('[feed:batchPrice] refresh failed', err);
        return;
      }

      if ((Number(store.loadGeneration) || 0) !== gen) return;
      if (!Array.isArray(store.rawRows) || !store.rawRows.length) return;

      const applied = applyBatchPriceMapToFeedRows(store.rawRows, batchMap);
      if (applied) {
        // 浅拷贝数组触发 presentation computed 重映射；不整表重拉
        store.rawRows = store.rawRows.slice();
      }
    }
  } finally {
    inflightByStore.delete(store);
    const pending = pendingByStore.get(store);
    pendingByStore.delete(store);
    if (pending) scheduleFeedFairOddsRefresh(store, pending);
  }
}

/**
 * 调度 Feed FairOdds 批量刷新：idle / 下一 macrotask，不阻塞首屏与 fetchPage。
 * @param {object} store 具备 rawRows / loadGeneration / listLoading 的 Pinia feed store
 * @param {object} [options]
 */
export function scheduleFeedFairOddsRefresh(store, options = {}) {
  if (!store) return;
  deferIdle(() => {
    void runFeedFairOddsRefresh(store, options);
  });
}
